// Guided Documentation modal — Phase 2 Session A.
// Opens from the dashboard Incomplete badge. Runs existing photos through
// identify-appliance v10 (merge mode) to extract anything we missed,
// then either celebrates completion or prompts for a fresh photo.

import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../supabase'
import { compressImage } from '../imageUtils'
import { stripExif } from '../utils/stripExif'
import { fetchPhotoAsBase64 } from '../utils/photoUtils'
import { NAME_BY_TYPE } from '../utils/applianceQueries'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

const SLOW_RERUN_MS = 6000

function snapshotFields(a) {
  const s = a?.specifications || {}
  return {
    make: a?.make || null,
    model: a?.model || null,
    serial: a?.serial || null,
    manufactured: a?.manufactured || null,
    series: s.series || null,
    platform: s.platform || null,
    features: Array.isArray(s.features) ? [...s.features] : [],
  }
}

function diffPickedUp(before, after) {
  const picked = []
  for (const k of ['make', 'model', 'serial', 'manufactured', 'series', 'platform']) {
    if (!before[k] && after[k]) picked.push({ key: k, value: after[k] })
  }
  const beforeSet = new Set(before.features.map(v => String(v).trim().toLowerCase()))
  const newFeatures = (after.features || []).filter(v => !beforeSet.has(String(v).trim().toLowerCase()))
  if (newFeatures.length > 0) picked.push({ key: 'features', value: newFeatures.join(', ') })
  return picked
}

function missingGates(after, photosLen) {
  const gates = []
  if (!after.make) gates.push('make')
  if (!after.model) gates.push('the model number')
  if (photosLen === 0) gates.push('a photo')
  return gates
}

function FIELD_LABEL(k) {
  return ({
    make: 'make',
    model: 'model',
    serial: 'serial number',
    manufactured: 'manufacture date',
    series: 'series',
    platform: 'platform',
    features: 'features',
  })[k] || k
}

const MANUAL_FIELDS = ['make', 'model', 'serial', 'manufactured']

function manualLabel(k) {
  return ({
    make: 'Make',
    model: 'Model number',
    serial: 'Serial number',
    manufactured: 'Manufacture date',
  })[k] || k
}

function manualPlaceholder(k) {
  return ({
    make: 'e.g. Kenmore',
    model: 'e.g. 110.27432600',
    serial: 'e.g. CW1234567',
    manufactured: 'e.g. 06/2018',
  })[k] || ''
}

export default function GuidedDocumentationModal({ appliance: initialAppliance, onClose, onUpdated }) {
  const typeLabel = (NAME_BY_TYPE[initialAppliance.type] || initialAppliance.name || 'appliance').toLowerCase()
  const snapshotRef = useRef(snapshotFields(initialAppliance))

  // Local mutable copy of the appliance — updated optimistically from each v10 merge response.
  const [appliance, setApplianceLocal] = useState(initialAppliance)
  const [step, setStep] = useState('rerunning')         // 'rerunning' | 'results' | 'capturing' | 'manual_entry'
  const [progress, setProgress] = useState({ done: 0, total: (initialAppliance.photos || []).length })
  const [slow, setSlow] = useState(false)
  const [error, setError] = useState(null)
  const [scanning, setScanning] = useState(false)
  const [manualValues, setManualValues] = useState({})
  const [savingManual, setSavingManual] = useState(false)

  const fileInputRef = useRef(null)
  const cancelledRef = useRef(false)

  // Slow-timer (total elapsed, not per-photo).
  useEffect(() => {
    if (step !== 'rerunning') return
    const t = setTimeout(() => setSlow(true), SLOW_RERUN_MS)
    return () => clearTimeout(t)
  }, [step])

  // Auto-run rerun on mount.
  //
  // CRITICAL: reset cancelledRef.current = false at the top of the effect. React 19's
  // StrictMode intentionally mounts every component twice in dev — mount #1's cleanup
  // sets the flag to true, and without this reset mount #2 inherits the stale flag and
  // every iteration of runReanalyze() bails on its first cancel check, so no fetch ever
  // fires.
  useEffect(() => {
    cancelledRef.current = false
    runReanalyze()
    return () => { cancelledRef.current = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function callV10WithBase64(base64, mediaType) {
    const hint = { type: appliance.type }
    if (appliance.make) hint.make = appliance.make
    const { data, error: fnErr } = await supabase.functions.invoke('identify-appliance', {
      body: {
        imageBase64: base64,
        mediaType: mediaType || 'image/jpeg',
        asset_id: appliance.asset_id,
        hint,
      },
    })
    if (fnErr) throw new Error(fnErr.message || 'identify-appliance failed')
    return data
  }

  function applyMerged(merged) {
    if (!merged) return
    setApplianceLocal(prev => ({
      ...prev,
      make: merged.make ?? prev.make,
      model: merged.model ?? prev.model,
      serial: merged.serial ?? prev.serial,
      manufactured: merged.manufactured ?? prev.manufactured,
      type: merged.type ?? prev.type,
      specifications: merged.specifications ?? prev.specifications,
    }))
  }

  async function runReanalyze() {
    const photos = appliance.photos || []
    setProgress({ done: 0, total: photos.length })
    if (photos.length === 0) {
      setStep('results')
      return
    }
    try {
      for (let i = 0; i < photos.length; i++) {
        if (cancelledRef.current) return
        const url = photos[i]
        try {
          const { base64, mediaType } = await fetchPhotoAsBase64(url)
          if (cancelledRef.current) return
          const resp = await callV10WithBase64(base64, mediaType)
          applyMerged(resp?.merged)
        } catch (e) {
          // Continue with remaining photos; surface only at the end if needed.
          console.warn(`[guided-doc] photo ${i + 1} rerun failed:`, e?.message || e)
        }
        if (cancelledRef.current) return
        setProgress(p => ({ ...p, done: p.done + 1 }))
      }
    } finally {
      if (!cancelledRef.current) {
        if (typeof onUpdated === 'function') { try { await onUpdated() } catch {} }
        setStep('results')
      }
    }
  }

  function skipAhead() {
    cancelledRef.current = true
    setStep('results')
    if (typeof onUpdated === 'function') { try { onUpdated() } catch {} }
  }

  async function handleFileSelected(e) {
    const file = e.target.files?.[0]
    if (e.target) e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please choose an image.'); return }
    setError(null)
    setScanning(true)
    try {
      // compressImage returns a File (image/jpeg). Use it directly for upload,
      // then read it as base64 for the v10 invocation.
      const stripped = await stripExif(file)
      const compressedFile = await compressImage(stripped, 1600, 0.80)
      const base64 = await blobToBase64(compressedFile)

      // Upload to appliance-photos bucket so the new photo persists in the gallery.
      const userId = appliance.created_by || (await supabase.auth.getUser()).data?.user?.id
      const filename = `${userId || 'anon'}-${appliance.asset_id}-${Date.now()}.jpg`
      let publicUrl = null
      try {
        const { error: upErr } = await supabase.storage
          .from('appliance-photos')
          .upload(filename, compressedFile, { upsert: true, contentType: 'image/jpeg' })
        if (upErr) throw upErr
        const { data: pub } = supabase.storage.from('appliance-photos').getPublicUrl(filename)
        publicUrl = pub?.publicUrl || null
      } catch (e) {
        console.warn('[guided-doc] photo upload failed, proceeding with AI extraction only:', e?.message || e)
      }

      if (publicUrl) {
        const nextPhotos = [...(appliance.photos || []), publicUrl]
        const { error: dbErr } = await supabase
          .from('appliance_assets')
          .update({ photos: nextPhotos })
          .eq('id', appliance.asset_id)
        if (!dbErr) {
          setApplianceLocal(prev => ({ ...prev, photos: nextPhotos }))
        }
      }

      const resp = await callV10WithBase64(base64, 'image/jpeg')
      applyMerged(resp?.merged)
      if (typeof onUpdated === 'function') { try { await onUpdated() } catch {} }
      setStep('results')
    } catch (e) {
      setError(`Scan failed: ${e?.message || 'unknown error'}`)
    } finally {
      setScanning(false)
    }
  }

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const r = new FileReader()
      r.onload = () => resolve(String(r.result).split(',')[1] || '')
      r.onerror = reject
      r.readAsDataURL(blob)
    })
  }

  async function saveManual() {
    const entries = Object.entries(manualValues)
      .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
      .filter(([, v]) => v != null && v !== '')
    if (entries.length === 0) { setStep('results'); return }
    setSavingManual(true)
    setError(null)
    try {
      const update = Object.fromEntries(entries)
      update.spec_source = 'manual_entry'
      const { error: dbErr } = await supabase
        .from('appliance_assets')
        .update(update)
        .eq('id', appliance.asset_id)
      if (dbErr) throw dbErr
      setApplianceLocal(prev => ({ ...prev, ...Object.fromEntries(entries), spec_source: 'manual_entry' }))
      if (typeof onUpdated === 'function') { try { await onUpdated() } catch {} }
      setManualValues({})
      setStep('results')
    } catch (e) {
      setError(`Save failed: ${e?.message || 'unknown error'}`)
    } finally {
      setSavingManual(false)
    }
  }

  // ── Render ──────────────────────────────────────────────────────────

  const afterSnap = snapshotFields(appliance)
  const picked = useMemo(() => diffPickedUp(snapshotRef.current, afterSnap),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appliance])
  const photosLen = (appliance.photos || []).length
  const gates = missingGates(afterSnap, photosLen)
  const isComplete = gates.length === 0

  return (
    <div onClick={onClose} style={S.backdrop}>
      <div onClick={(e) => e.stopPropagation()} style={S.card}>
        <button onClick={onClose} aria-label="Close" style={S.closeBtn}>×</button>

        {step === 'rerunning' && (
          <>
            <div style={S.h1}>Let me take another look at your {typeLabel}…</div>
            <div style={S.muted}>
              Re-analyzing photo {Math.min(progress.done + 1, Math.max(progress.total, 1))} of {Math.max(progress.total, 1)}
            </div>
            {slow && (
              <div style={S.slow}>
                <div style={{ fontSize: '13px', color: C.muted, marginBottom: '8px' }}>
                  Almost done — checking the last details…
                </div>
                <button style={S.secondaryBtn} onClick={skipAhead}>Skip ahead</button>
              </div>
            )}
          </>
        )}

        {step === 'capturing' && (
          <>
            <div style={S.h1}>Photograph the data plate</div>
            <div style={S.body}>
              Usually a small white or silver sticker with small text. Look on the back, inside the door, or behind a kick-plate.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
              <button
                style={S.primaryBtn}
                disabled={scanning}
                onClick={() => fileInputRef.current?.click()}
              >
                {scanning ? 'Reading photo…' : 'Open camera'}
              </button>
              <button style={S.secondaryBtn} disabled={scanning} onClick={() => setStep('results')}>
                Back
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
            {error && <div style={S.error}>{error}</div>}
          </>
        )}

        {step === 'results' && (
          <>
            {isComplete ? (
              <>
                <div style={{ ...S.h1, color: C.success }}>✓ Your {typeLabel} is now fully documented</div>
                {picked.length > 0 && (
                  <div style={S.body}>
                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>What I picked up this round:</div>
                    <ul style={S.list}>
                      {picked.map((p, i) => (
                        <li key={i}>{FIELD_LABEL(p.key)}: <strong>{p.value}</strong></li>
                      ))}
                    </ul>
                  </div>
                )}
                <button style={S.primaryBtn} onClick={onClose}>Close</button>
              </>
            ) : picked.length > 0 ? (
              <>
                <div style={S.h1}>Made some progress</div>
                <div style={S.body}>
                  <div style={{ fontWeight: 600, marginBottom: '6px' }}>I picked up:</div>
                  <ul style={S.list}>
                    {picked.map((p, i) => (
                      <li key={i}>{FIELD_LABEL(p.key)}: <strong>{p.value}</strong></li>
                    ))}
                  </ul>
                  <div style={{ marginTop: '10px' }}>
                    <span style={{ fontWeight: 600 }}>I still need: </span>
                    {gates.join(', ')}.
                  </div>
                </div>
                <button style={S.primaryBtn} onClick={() => setStep('capturing')}>Take a photo</button>
              </>
            ) : (
              <>
                <div style={S.h1}>I couldn't pull more from your existing photos</div>
                <div style={S.body}>
                  Still need: {gates.join(', ')}.
                </div>
                <button style={S.primaryBtn} onClick={() => setStep('capturing')}>Take a photo</button>
                <button
                  style={S.manualEntryLink}
                  onClick={() => { setManualValues({}); setError(null); setStep('manual_entry') }}
                >
                  or, enter it manually →
                </button>
              </>
            )}
            {error && <div style={S.error}>{error}</div>}
          </>
        )}

        {step === 'manual_entry' && (
          <>
            <div style={S.h1}>Enter what you know</div>
            <div style={{ marginBottom: '14px' }}>
              {MANUAL_FIELDS.filter(k => !appliance[k]).map(field => (
                <div key={field} style={{ marginBottom: '10px' }}>
                  <label style={S.manualLabel}>{manualLabel(field)}</label>
                  <input
                    type="text"
                    value={manualValues[field] || ''}
                    placeholder={manualPlaceholder(field)}
                    onChange={(e) => setManualValues(v => ({ ...v, [field]: e.target.value }))}
                    style={S.manualInput}
                    autoFocus={field === MANUAL_FIELDS.find(k => !appliance[k])}
                  />
                </div>
              ))}
              {MANUAL_FIELDS.filter(k => !appliance[k]).length === 0 && (
                <div style={S.body}>Nothing left to fill in by hand — only a photo would help now.</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={{ ...S.primaryBtn, flex: 1, marginTop: 0 }} disabled={savingManual} onClick={saveManual}>
                {savingManual ? 'Saving…' : 'Save'}
              </button>
              <button style={S.secondaryBtn} disabled={savingManual} onClick={() => { setManualValues({}); setStep('results') }}>
                Cancel
              </button>
            </div>
            {error && <div style={S.error}>{error}</div>}
          </>
        )}
      </div>
    </div>
  )
}

const S = {
  backdrop: {
    // Higher than ApplianceDetail's modal (1000) so we always win the stacking
    // context if both happen to be open simultaneously.
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px', zIndex: 1100,
  },
  card: {
    position: 'relative', background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '14px', padding: '28px 24px 22px', maxWidth: '460px', width: '100%',
    boxShadow: '0 12px 40px rgba(44,29,20,0.25)', fontFamily: fs, color: C.brown,
  },
  closeBtn: {
    position: 'absolute', top: '10px', right: '12px',
    background: 'transparent', border: 'none', color: C.muted,
    fontSize: '22px', cursor: 'pointer', lineHeight: 1, padding: '4px 8px',
    fontFamily: fs,
  },
  h1: { fontFamily: f, fontSize: '18px', color: C.brown, marginBottom: '10px', lineHeight: '1.35' },
  body: { fontSize: '14px', color: C.brown, lineHeight: '1.5', marginBottom: '14px' },
  muted: { fontSize: '13px', color: C.muted, marginBottom: '8px' },
  list: { margin: '0 0 0 18px', padding: 0, lineHeight: '1.6' },
  slow: { marginTop: '14px', borderTop: `1px dashed ${C.border}`, paddingTop: '12px' },
  primaryBtn: {
    width: '100%', background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    color: C.card, border: 'none', borderRadius: '10px',
    padding: '12px', fontSize: '14px', fontFamily: f, fontWeight: 700,
    letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer',
    marginTop: '4px',
  },
  secondaryBtn: {
    background: 'transparent', color: C.amber, border: `1px solid ${C.amber}`,
    borderRadius: '10px', padding: '10px 14px', fontSize: '13px',
    fontFamily: fs, fontWeight: 600, cursor: 'pointer',
  },
  error: { marginTop: '10px', fontSize: '12px', color: C.danger, fontFamily: fs },
  manualEntryLink: {
    background: 'transparent', border: 'none', color: C.brown,
    fontSize: '12px', fontFamily: fs, textDecoration: 'underline',
    cursor: 'pointer', padding: '10px 0 0', display: 'block',
    width: '100%', textAlign: 'center',
  },
  manualLabel: {
    fontSize: '10px', color: C.muted, letterSpacing: '1.5px',
    textTransform: 'uppercase', display: 'block', marginBottom: '4px',
    fontFamily: fs,
  },
  manualInput: {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '10px 12px', fontSize: '14px',
    fontFamily: f, color: C.brown, boxSizing: 'border-box', outline: 'none',
  },
}
